import { industryTranslations } from '@/doi-tac-module/constants/industry-translations';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';

// Fuzzy keywords mapping based on common Bangladesh CV terms
const fuzzyJobMapping: Record<string, string> = {
    'sewing': 'Thợ may',
    'tailor': 'Thợ may',
    'garment': 'Thợ may',
    'cleaner': 'Vệ sinh tòa nhà',
    'cleaning': 'Vệ sinh tòa nhà',
    'construction': 'Xây dựng',
    'builder': 'Xây dựng',
    'welder': 'Thợ hàn',
    'welding': 'Thợ hàn',
    'fitter': 'Thợ cơ khí',
    'operator': 'Vận hành máy',
    'machine': 'Vận hành máy',
    'helper': 'Lao động phổ thông',
    'laborer': 'Lao động phổ thông',
    'driver': 'Lái xe'
};

export function calculateDurationFromDates(work: any): string | null {
    if (!work) return null;
    let startRaw = work.startDate;
    let endRaw = work.isCurrent ? null : work.endDate;
    
    // Nếu startRaw là Timestamp của firebase: { _seconds: ..., _nanoseconds: ... } hoặc { seconds: ... }
    if (startRaw && typeof startRaw === 'object' && ('seconds' in startRaw || '_seconds' in startRaw)) {
        startRaw = startRaw.toDate ? startRaw.toDate() : new Date((startRaw.seconds || startRaw._seconds) * 1000);
    }
    if (endRaw && typeof endRaw === 'object' && ('seconds' in endRaw || '_seconds' in endRaw)) {
        endRaw = endRaw.toDate ? endRaw.toDate() : new Date((endRaw.seconds || endRaw._seconds) * 1000);
    }
    
    const isCurrent = !!work.isCurrent;
    const start = startRaw ? new Date(startRaw) : null;
    const end = isCurrent ? new Date() : (endRaw ? new Date(endRaw) : null);
    
    if (start && !isNaN(start.getTime()) && end && !isNaN(end.getTime())) {
        const totalMonths = Math.max(0, Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));
        const y = Math.floor(totalMonths / 12);
        const m = totalMonths % 12;
        
        if (y > 0 && m > 0) return `${y} năm ${m} tháng`;
        if (y > 0) return `${y} năm`;
        if (m > 0) return `${m} tháng`;
    }
    return null;
}


/**
 * Parses raw AI-extracted CV experience strings (e.g. "WORKED IN BANGLADESH AS A SEWING OPERATOR FOR 05 YEARS")
 * into normalized job titles and durations.
 */
export function parseBangladeshExperience(rawString: string, defaultIndustry: string = 'Lao động phổ thông'): { jobTitle: string, duration: string } {
    if (!rawString) return { jobTitle: defaultIndustry, duration: '1 năm' };
    
    const parsedRaw = rawString.trim().toLowerCase();
    
    // Case 1: Standard AI format variants
    // Xử lý tốt các dạng: "WORKED IN BANGLADESH AS A SEWING OPERATOR FOR 05 YEARS"
    // "Worked as a sewing operator for 15 years"
    // "WORKED AS SEWING OPERATOR FOR 5-YEARS"
    const standardRegex = /worked(?: (?:in|at) .+?)? as (?:a |an |the )?(.+?) for ([\d\-\s]+)\s*(?:-|)(years?|months?|yrs?|mos?)/i;
    const match = rawString.match(standardRegex);
    
    if (match) {
        let [, jobTitle, durationVal, durationUnit] = match;
        
        jobTitle = jobTitle.trim();
        durationVal = durationVal.replace(/-/g, '').trim(); 
        
        const durInt = parseInt(durationVal, 10);
        if (!isNaN(durInt)) durationVal = durInt.toString();
        
        let unitVi = 'năm';
        if (durationUnit.toLowerCase().includes('month') || durationUnit.toLowerCase().includes('mo')) {
            unitVi = 'tháng';
        }
        
        return { jobTitle: mapJobTitle(jobTitle, defaultIndustry), duration: `${durationVal} ${unitVi}` };
    }
    
    // Case 2: Just a job title (e.g., "SEWING MACHINE OPERATOR", "CLEANER")
    if (rawString.length < 50 && !parsedRaw.includes('worked') && !parsedRaw.includes('since')) {
         return { jobTitle: mapJobTitle(rawString, defaultIndustry), duration: '1 năm' }; // uses fallback flag
    }

    // Case 3: Messy grammar / Keyword matching fallback
    for (const [key, translated] of Object.entries(fuzzyJobMapping)) {
        if (parsedRaw.includes(key)) {
            return { jobTitle: translated, duration: '1 năm' };
        }
    }
    
    // Case 4: Complete fallback to desired industry
    return { jobTitle: mapJobTitle(defaultIndustry, defaultIndustry), duration: '1 năm' };
}

function mapJobTitle(rawJob: string, defaultIndustry: string): string {
    const jobKey = rawJob.trim().toLowerCase();
    
    // Check fuzzy match
    for (const [key, translated] of Object.entries(fuzzyJobMapping)) {
         if (jobKey.includes(key)) return translated;
    }
    
    // Check if it exists in translation dictionary
    if ((industryTranslations as any)[rawJob]?.['vi']) {
        return (industryTranslations as any)[rawJob]['vi'];
    }
    
    // Fallback: capitalize first letters
    if (rawJob && rawJob.length <= 40) {
        return rawJob.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    }
    
    return defaultIndustry || 'Lao động phổ thông';
}

/**
 * Returns formatted string for Candidate Card badge.
 * Output: "Kinh nghiệm: Thợ may - 5 năm"
 */
export function formatExperienceBadge(candidate: any, language: 'vi'|'en'|'bn'|'ja'|'tw' = 'vi'): string {
    const history = candidate.workHistory || [];
    if (!history || history.length === 0) {
        const noExpMap: Record<string, string> = {
            vi: 'Chưa có kinh nghiệm',
            en: 'No experience',
            bn: 'কোন অভিজ্ঞতা নেই',
            ja: '経験なし',
            tw: '無工作經驗'
        };
        return noExpMap[language] || 'Chưa có kinh nghiệm';
    }
    
    const latestWork = history[0] || {};
    const latestExp = candidate.experience?.[0] || {};
    
    const fallbackIndustry = latestWork.industry || candidate.desiredIndustry || 'Lao động phổ thông';
    
    // Mảng thu thập tất cả các chuỗi có khả năng chứa dạng "WORKED IN ... FOR 05 YEARS" của AI
    const potentialStrings = [
        latestWork.jobDetails?.[0],
        latestExp.jobDetails?.[0],
        latestExp.description,
        latestExp.company,
        latestWork.companyName,
        latestWork.industry,
        latestExp.role
    ].filter(Boolean);
    
    // Thợ săn chuỗi: Quét lần lượt tới khi tìm thấy chuỗi nào chứa mốc thời gian rõ ràng
    let parsed = { jobTitle: '', duration: '1 năm' };
    let foundRealDuration = false;
    
    for (const str of potentialStrings) {
        if (typeof str === 'string' && str.trim() !== '') {
            const result = parseBangladeshExperience(str, fallbackIndustry);
            // Ưu tiên lưu Job Title đầu tiên hợp lệ
            if (!parsed.jobTitle || parsed.jobTitle === 'Lao động phổ thông') {
                parsed.jobTitle = result.jobTitle;
            }
            // Ngay khi Regex bắt được mốc thời gian lệch khỏi mức default (tức là có năm)
            if (result.duration !== '1 năm') {
                parsed.duration = result.duration;
                parsed.jobTitle = result.jobTitle; // Ưu tiên Job Title từ câu chuỗi chuẩn nhất
                foundRealDuration = true;
                break;
            }
        }
    }
    
    // Đảm bảo không bị rỗng jobTitle nếu tất cả các mảng đều lỗi
    if (!parsed.jobTitle) {
        const fall = parseBangladeshExperience('', fallbackIndustry);
        parsed.jobTitle = fall.jobTitle;
    }

    
    // Identify candidate's manually listed overall experience if specific dates fail
    const manualExpNum = candidate.yearsOfExperienceNum && candidate.yearsOfExperienceNum > 0 ? `${candidate.yearsOfExperienceNum} năm` : null;
    const manualExpString = (candidate.yearsOfExperience && typeof candidate.yearsOfExperience === 'string' && candidate.yearsOfExperience !== 'None') ? candidate.yearsOfExperience : null;
    
    // Determine the most accurate duration
    let finalDuration = parsed.duration;
    // If the regex returned the default '1 năm' fallback, we should seek real numbers from the DB
    if (parsed.duration === '1 năm') {
        const calculatedDate = calculateDurationFromDates(latestWork);
        if (calculatedDate) {
            finalDuration = calculatedDate;
        } else if (latestWork.period && latestWork.period.toLowerCase() !== 'undefined' && latestWork.period.trim() !== '') {
            // Check if period is just a number like "5"
            if (/^\d+$/.test(latestWork.period)) {
                finalDuration = `${latestWork.period} năm`;
            } else {
                finalDuration = latestWork.period.replace(/years?/i, 'năm').replace(/months?/i, 'tháng');
            }
        } else if (latestWork.durationLabel) {
            finalDuration = latestWork.durationLabel;
        } else if (manualExpNum) {
            finalDuration = manualExpNum;
        } else if (manualExpString) {
            finalDuration = manualExpString;
        } else {
            // No time reference found anywhere! Instead of defaulting to '1 năm', clear it.
            finalDuration = '';
        }
    }

    
    let translatedJob = parsed.jobTitle;
    
    // Try to translate the resolved JobTitle into target language
    const industryDict = (industryTranslations as any)[translatedJob];
    const agencyDict = (agencyTranslations.candidateSearch.industryNames as any)[translatedJob];
    if (industryDict?.[language]) translatedJob = industryDict[language];
    else if (agencyDict?.[language]) translatedJob = agencyDict[language];
    // If target is NOT vietnamese, we might need a fallback translation map
    else if (language !== 'vi') {
        const fallbackTrans: any = {
            'Thợ may': { en: 'Sewing Operator', ja: '縫製工', tw: '成衣工', bn: 'সেলাই অপারেটর' },
            'Vệ sinh tòa nhà': { en: 'Cleaner', ja: 'ビルクリーニング', tw: '清潔工', bn: 'ক্লিনার' },
            'Xây dựng': { en: 'Construction Worker', ja: '建設作業員', tw: '建築工人', bn: 'নির্মাণ শ্রমিক' },
            'Thợ hàn': { en: 'Welder', ja: '溶接工', tw: '焊接工', bn: 'ওয়েল্ডার' },
            'Thợ cơ khí': { en: 'Fitter / Mechanic', ja: '整備士', tw: '技工', bn: 'মেকানিক' },
            'Vận hành máy': { en: 'Machine Operator', ja: '機械オペレーター', tw: '機器操作員', bn: 'মেশিন অপারেটর' },
            'Lao động phổ thông': { en: 'General Labor', ja: '一般労働者', tw: '普通勞工', bn: 'সাধারণ শ্রম' },
            'Lái xe': { en: 'Driver', ja: '運転手', tw: '司機', bn: 'ড্রাইভার' }
        };
        if (fallbackTrans[translatedJob]?.[language]) {
            translatedJob = fallbackTrans[translatedJob][language];
        }
    }

    let translatedDuration = finalDuration;
    if (language === 'bn') {
        translatedDuration = finalDuration.replace('năm', 'বছর').replace('tháng', 'মাস');
    } else if (language === 'en') {
        translatedDuration = finalDuration.replace('năm', 'years').replace('tháng', 'months');
    } else if (language === 'ja') {
        translatedDuration = finalDuration.replace('năm', '年').replace('tháng', 'ヶ月').replace(' ', ''); // 5年 instead of 5 年
    } else if (language === 'tw') {
        translatedDuration = finalDuration.replace('năm', '年').replace('tháng', '個月').replace(' ', '');
    }
    
    const prefix = {
        vi: 'Kinh nghiệm:',
        en: 'Experience:',
        ja: '経験:',
        bn: 'অভিজ্ঞতা:',
        tw: '經驗:'
    }[language] || 'Kinh nghiệm:';
    
    // Nếu rỗng thời gian (không bắt được ở DB hay Regex) -> Xóa phần hiển thị thời gian
    if (!translatedDuration || translatedDuration.trim() === '') {
        return `${prefix} ${translatedJob}`;
    }
    
    return `${prefix} ${translatedJob} - ${translatedDuration}`;
}

/**
 * Parses details for profile view. Used to convert long ALL CAPS english sentences
 * into neat translated strings like 'Thợ may (5 năm)'.
 */
export function formatProfileExperience(rawDetail: string, candidate: any, language: 'vi'|'en'|'bn'|'ja'|'tw' = 'vi'): string {
    const fallbackIndustry = candidate?.desiredIndustry || 'Lao động phổ thông';
    const parsed = parseBangladeshExperience(rawDetail, fallbackIndustry);
    
    if (!parsed || !parsed.jobTitle) return rawDetail;
    
    let translatedJob = parsed.jobTitle;
    if (language !== 'vi') {
         const fallbackTrans: any = {
            'Thợ may': { en: 'Sewing Operator', ja: '縫製工', tw: '成衣工', bn: 'সেলাই অপারেটর' },
            'Vệ sinh tòa nhà': { en: 'Cleaner', ja: 'ビルクリーニング', tw: '清潔工', bn: 'ক্লিনার' },
            'Xây dựng': { en: 'Construction', ja: '建設', tw: '營造', bn: 'নির্মাণ' },
            'Thợ hàn': { en: 'Welder', ja: '溶接工', tw: '焊接工', bn: 'ওয়েল্ডার' },
            'Thợ cơ khí': { en: 'Fitter', ja: '整備士', tw: '技工', bn: 'মেকানিক' },
            'Vận hành máy': { en: 'Machine Operator', ja: '機械オペレーター', tw: '機器操作員', bn: 'মেশিন অপারেটর' },
            'Lao động phổ thông': { en: 'General Labor', ja: '一般労働', tw: '普通勞工', bn: 'সাধারণ শ্রম' },
            'Lái xe': { en: 'Driver', ja: '運転手', tw: '司機', bn: 'ড্রাইভার' }
        };
        if (fallbackTrans[translatedJob]?.[language]) {
            translatedJob = fallbackTrans[translatedJob][language];
        }
    }

    let translatedDuration = parsed.duration;
    
    // If fallback is '1 năm' but the string didn't have numbers, fetch from real dates
    if (parsed.duration === '1 năm' && !/\d/.test(rawDetail)) {
        const latestWork = candidate.workHistory?.find((w: any) => w.jobDetails?.includes(rawDetail)) || candidate.workHistory?.[0];
        let actualDur = '';
        if (latestWork) {
            actualDur = calculateDurationFromDates(latestWork) || latestWork.period || '';
            if (/^\d+$/.test(actualDur)) actualDur += ' năm';
            else actualDur = actualDur.replace(/years?/i, 'năm').replace(/months?/i, 'tháng');
        }
        if (!actualDur && candidate.yearsOfExperienceNum > 0) {
            actualDur = `${candidate.yearsOfExperienceNum} năm`;
        }
        if (!actualDur && candidate.yearsOfExperience && typeof candidate.yearsOfExperience === 'string' && candidate.yearsOfExperience !== 'None') {
            actualDur = candidate.yearsOfExperience;
        }
        translatedDuration = actualDur;
    }
    
    if (translatedDuration) {
        if (language === 'bn') {
            translatedDuration = translatedDuration.replace('năm', 'বছর').replace('tháng', 'মাস');
        } else if (language === 'en') {
            translatedDuration = translatedDuration.replace('năm', 'years').replace('tháng', 'months');
        } else if (language === 'ja') {
            translatedDuration = translatedDuration.replace('năm', '年').replace('tháng', 'ヶ月').replace(' ', '');
        } else if (language === 'tw') {
            translatedDuration = translatedDuration.replace('năm', '年').replace('tháng', '個月').replace(' ', '');
        }
    }
    
    if (!translatedDuration || translatedDuration.trim() === '') {
        return translatedJob;
    }
    
    return `${translatedJob} (${translatedDuration})`;
}

/**
 * Calculates candidate age from available fields.
 * Priority: age > birthYear > dateOfBirth
 */
export function calculateAge(candidate: any): number | string {
    if (!candidate) return '?';
    
    // 1. Direct age field (Mock data or manual entry)
    if (candidate.age && typeof candidate.age === 'number') return candidate.age;
    if (candidate.age && typeof candidate.age === 'string' && !isNaN(parseInt(candidate.age))) return parseInt(candidate.age);
    
    const pi = candidate.personalInfo || {};
    const currentYear = new Date().getFullYear();
    
    // 2. Birth year
    if (pi.birthYear && typeof pi.birthYear === 'number') return currentYear - pi.birthYear;
    if (pi.birthYear && typeof pi.birthYear === 'string' && !isNaN(parseInt(pi.birthYear))) return currentYear - parseInt(pi.birthYear);
    
    // 3. Date of birth
    const dob = pi.dateOfBirth || candidate.dateOfBirth;
    if (dob) {
        const birthDate = new Date(dob);
        if (!isNaN(birthDate.getTime())) {
            let age = currentYear - birthDate.getFullYear();
            const m = new Date().getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && new Date().getDate() < birthDate.getDate())) {
                age--;
            }
            return age;
        }
    }
    
    return '?';
}

